import { Paper, PaperProps } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { shadows } from 'c1ui/theme/C1Theme'
import { C1Link } from 'components/core/links/C1Link'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { Route } from 'lib/routes'
import { FC, KeyboardEvent, MouseEvent } from 'react'

export interface C1CardProps extends PaperProps {
    href?: Route
    isSelected?: boolean
    ariaLabel?: string
    hasInteractiveChildren?: boolean
}

const baseCardSx = {
    padding: 4,
    marginBottom: 4,
    borderRadius: C1BorderRadius.large,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    color: 'text.primary',
    boxShadow: shadows.default,
    transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s',
}

export const C1Card: FC<C1CardProps> = ({
    children,
    sx,
    href,
    onClick,
    isSelected,
    tabIndex,
    ariaLabel,
    hasInteractiveChildren = false,
    ...props
}) => {
    const isClickable = href || onClick

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
        if (!onClick || href) return
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick(e as unknown as MouseEvent<HTMLDivElement>)
        }
    }

    const cardSx = {
        ...baseCardSx,
        ...(isClickable && {
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
                borderColor: 'primary.main',
                boxShadow: shadows.strong,
            },
            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '2px',
                boxShadow: (theme) => `${shadows.default}, inset 0 0 0 1px ${theme.palette.primary.dark}`,
            },
        }),
        '&[data-active]': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.extraLight',
            '&:hover': {
                boxShadow: (theme) => `${shadows.default}, inset 0 0 0 1px ${theme.palette.primary.dark}`,
            },
            '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.dark',
                outlineOffset: '2px',
            },
        },
    }

    const baseProps = {
        sx: sxc(cardSx, sx),
        ...(isSelected && {
            'data-active': '',
            'aria-current': true as const,
        }),
        ...(ariaLabel && { 'aria-label': ariaLabel }),
        ...props,
    }

    if (href) {
        return <Paper
            component={C1Link}
            href={href}
            tabIndex={tabIndex ?? 0}
            onClick={onClick}
            {...baseProps}
        >
            {children}
        </Paper>
    }

    if (onClick) {
        return <Paper
            component="div"
            role={hasInteractiveChildren ? undefined : 'button'}
            tabIndex={hasInteractiveChildren ? tabIndex : (tabIndex ?? 0)}
            onClick={onClick}
            onKeyDown={hasInteractiveChildren ? undefined : handleKeyDown}
            {...baseProps}
        >
            {children}
        </Paper>
    }

    return <Paper tabIndex={tabIndex} {...baseProps}>
        {children}
    </Paper>
}
